/**
 * The story scene before level 1, as the C64 draws it (storyText.ts): a black sky scattered with
 * blue and cyan star dots, and Bub and Bob each floating in a big bubble either side of the middle,
 * below the four lines of text (which the HUD draws). On the C64 each dragon swaps between two
 * pictures every 8 frames - arms paddling, the bubble's shine shifting - Bub and Bob in opposite phase;
 * here the arms paddle and the bubbles breathe smoothly on that same 0.32 s beat, and float a little.
 */

import { Group, Mesh, SphereGeometry } from 'three';
import { buildDragon } from '../actors/dragon';
import type { Puppet } from '../actors/puppet';
import type { Palette } from '../palette';
import { buildStars, buildWall } from './backdrop';
import { STORY_SCENE } from './layout';
import type { TitleMaterials } from './materials';
import { PADDLE_SECONDS, poseFloating } from './titlePoses';

interface Floater {
  pup: Puppet;
  bubble: Mesh;
  side: -1 | 1;
  phase: 0 | 1;
}

export class StoryStage {
  readonly group = new Group();
  private readonly sphere = new SphereGeometry(STORY_SCENE.bubbleRadius, 48, 32);
  /** The wall and the stars. */
  private readonly backdrop: Mesh[];
  private readonly floaters: Floater[];

  constructor(p: Palette, m: TitleMaterials) {
    // The C64 scatters 200 star characters over its whole screen (`D_0885`): one-row dots in blue ($FE)
    // and two-row dots in cyan ($FF), each two pixels wide (a quarter of a tile). Not the `bob` ramp for
    // the blue: the 1987 look turns that cyan, P2's colour on the C64.
    this.backdrop = buildStars(p, m.glow, {
      count: 200, seed: 0x0885, layers: 2, z0: -1.9, dz: 0.4, halfW: 23, halfH: 13.6,
      kinds: [
        { colour: 'water.1', w: 0.25, h: 0.125 },
        { colour: 'antenna.1', w: 0.25, h: 0.25 },
      ],
    });
    this.backdrop.push(new Mesh(buildWall(p, 26, 16), m.wall));
    this.floaters = [
      this.floater(p, 'bub', m, -1, 0),
      this.floater(p, 'bob', m, 1, 1),
    ];
    this.group.add(...this.backdrop);
    for (const f of this.floaters) this.group.add(f.pup.root, f.bubble);
    this.update(0);
  }

  private floater(p: Palette, skin: 'bub' | 'bob', m: TitleMaterials, side: -1 | 1, phase: 0 | 1): Floater {
    const pup = buildDragon(p, skin, m.dragon);
    // Turned well toward the camera: the C64's pictures show the dragons face on.
    pup.turn = 1.2;
    pup.root.scale.setScalar(STORY_SCENE.dragonScale);
    const bubble = new Mesh(this.sphere, skin === 'bub' ? m.glassBub : m.glassBob);
    return { pup, bubble, side, phase };
  }

  update(t: number): void {
    for (const f of this.floaters) {
      const beat = Math.sin((t / PADDLE_SECONDS + f.phase * 0.5) * Math.PI * 2);
      const float = Math.sin(t * Math.PI * 2 * 0.45 + f.phase * Math.PI) * 0.12;
      const x = f.side * STORY_SCENE.dragonX;
      const y = STORY_SCENE.centreY + float;
      f.bubble.position.set(x, y, 0);
      f.bubble.scale.set(1 + beat * 0.02, 1 - beat * 0.02, 1);
      // The dragon's middle (a tile above its feet, scaled) at the bubble's centre.
      f.pup.root.position.set(x, y - STORY_SCENE.dragonScale, 0);
      poseFloating(f.pup, t, f.side === -1 ? 1 : -1, f.phase + 3, f.phase);
    }
  }

  dispose(): void {
    for (const s of this.backdrop) s.geometry.dispose();
    this.sphere.dispose();
    for (const f of this.floaters) f.pup.dispose();
  }
}
